import { routeSnapshotExtensions } from './route-extensions';
import { ActivatedRouteSnapshot } from '@angular/router';
import { SponsorStudySite } from '../sponsor/sites/sponsor-study-site';
import { CategoryNaturalKey } from '../documents/models/category';
import { SponsorStudy } from '../sponsor-dashboard/sponsor-study';
import { Site } from '../sites/site';
import { User } from '../user/user';
import { Settings } from '../core/settings';
import { SponsorStudySiteIdentity } from '../sponsor/sites/sponsor-study-site-identity';

export type ProtocolIdParameter      = { name: 'protocolId' };
export type ChildCategoryIdParameter = { name: 'childCategoryId' };
export type CSiteIdParameter         = { name: 'siteId' };
export type ZcTestCodeParameter      = { name: 'zcTestCode' };
export type StudyUrlParamParameter   = { name: 'studyUrlParam' };
export type CSiteUrlParamParameter   = { name: 'siteUrlParam' };
export type CategoryIdParameter      = { name: 'categoryId' };
export type EditedUserPkParameter    = { name: 'editedUserPk' };
export type ZcSubjectIdParameter       = { name: 'zcSubjectId'};

export type StudySitesData             = { name: 'studySites' };
export type SelectedStudyData          = { name: 'selectedStudy' };
export type SelectedSiteData           = { name: 'selectedSite' };
export type StudyPermissionsData       = { name: 'studyPermissions' };
export type DrawerBehaviorData         = { name: 'drawerBehavior' };
export type SiteSpecificCategoriesData = { name: 'siteSpecificCategories' };
export type UserData                   = { name: 'user' };
export type TabNameData                = { name: 'tabName' };
export type BoxPlotGroupSelectorData   = { name: 'boxPlotGroupSelector'};
export type BoxPlotXAxisTitle          = { name: 'boxPlotXAxisTitle'};
export type BoxPlotXTickRenderrer      = { name: 'boxPlotXTickRenderrer'};
export type BoxPlotXAxisSortingSelector     = { name: 'boxPlotXAxisSortingSelector'};
export type BoxPlotDataExtraFilter     = { name: 'boxPlotDataExtraFilter'};
export type FilterDefaultDateRange     = { name: 'filterDefaultDateRange' };
export type SponsorSubjectsItemsData   = { name: 'sponsorSubjectIdentities' };
export type StudySiteIdentitiesData    = { name: 'studySiteIdentities' };
export type SelectedSiteIdentityData   = { name: 'selectedSiteIdentity' };
export type SelectedStudyGroup         = { name: 'selectedStudyGroup' };

export type EQueryIdParameter          = { name: 'eQueryId' };
export type EQueryStageId              = { name: 'eQueryStageId'};
export type AccessionNumber            = { name: 'accessionNumber'};

type AllRouteParameters =
    | ProtocolIdParameter
    | ChildCategoryIdParameter
    | CSiteIdParameter
    | ZcTestCodeParameter
    | StudyUrlParamParameter
    | CSiteUrlParamParameter
    | CategoryIdParameter
    | EditedUserPkParameter
    | ZcSubjectIdParameter
    | TabNameData
    | EQueryIdParameter
    | EQueryStageId
    | AccessionNumber;

type AllRouteData =
    | StudySitesData
    | SelectedStudyData
    | SelectedSiteData
    | StudyPermissionsData
    | DrawerBehaviorData
    | SiteSpecificCategoriesData
    | UserData
    | BoxPlotGroupSelectorData
    | BoxPlotXAxisTitle
    | BoxPlotXTickRenderrer
    | BoxPlotXAxisSortingSelector
    | BoxPlotDataExtraFilter
    | FilterDefaultDateRange
    | SponsorSubjectsItemsData
    | StudySiteIdentitiesData
    | SelectedSiteIdentityData
    | SelectedStudyGroup
    ;

type ParamName<T> =
    T extends { name: infer N }
        ? N extends string
        ? N
        : never
        : never;

type ParamsType<T> = {
    readonly [K in ParamName<T>]: K
};

export const routeParams: ParamsType<AllRouteParameters> = {
    protocolId:      'protocolId',
    childCategoryId: 'childCategoryId',
    siteId:          'siteId',
    zcTestCode:      'zcTestCode',
    studyUrlParam:   'studyUrlParam',
    siteUrlParam:    'siteUrlParam',
    categoryId:      'categoryId',
    editedUserPk:    'editedUserPk',
    zcSubjectId:     'zcSubjectId',
    tabName:         'tabName',
    eQueryId:        'eQueryId',
    eQueryStageId:   'eQueryStageId',
    accessionNumber: 'accessionNumber',
};

export type CategoryParameters = ParamName<CategoryIdParameter | ChildCategoryIdParameter>;

export const routeData: ParamsType<AllRouteData> = {
    studySites:             'studySites',
    selectedStudy:          'selectedStudy',
    selectedSite:           'selectedSite',
    studyPermissions:       'studyPermissions',
    drawerBehavior:         'drawerBehavior',
    siteSpecificCategories: 'siteSpecificCategories',
    user:                   'user',
    boxPlotGroupSelector:   'boxPlotGroupSelector',
    boxPlotXAxisTitle:      'boxPlotXAxisTitle',
    boxPlotXTickRenderrer:  'boxPlotXTickRenderrer',
    boxPlotXAxisSortingSelector:    'boxPlotXAxisSortingSelector',
    boxPlotDataExtraFilter: 'boxPlotDataExtraFilter',
    filterDefaultDateRange: 'filterDefaultDateRange',
    sponsorSubjectIdentities:   'sponsorSubjectIdentities',
    studySiteIdentities:    'studySiteIdentities',
    selectedSiteIdentity: 'selectedSiteIdentity',
    selectedStudyGroup: 'selectedStudyGroup',
};

export function findProtocolId(route: ActivatedRouteSnapshot): number {
    return +routeSnapshotExtensions.findParameter<string>(route, routeParams.protocolId);
}

export function findSiteId(route: ActivatedRouteSnapshot): number {
    return +routeSnapshotExtensions.findParameter<string>(route, routeParams.siteId);
}

export function findEditedUserPk(route: ActivatedRouteSnapshot): number {
    return +routeSnapshotExtensions.findParameter<string>(route, routeParams.editedUserPk);
}

export function findCategoryId(route: ActivatedRouteSnapshot): CategoryNaturalKey {
    return routeSnapshotExtensions.findParameter<CategoryNaturalKey>(route, routeParams.categoryId);
}

export function findChildCategoryId(route: ActivatedRouteSnapshot): CategoryNaturalKey {
    return routeSnapshotExtensions.findParameter<CategoryNaturalKey>(route, routeParams.childCategoryId);
}

export function findStudySites(route: ActivatedRouteSnapshot): SponsorStudySite[] {
    return routeSnapshotExtensions.findData<SponsorStudySite[]>(route, routeData.studySites);
}

export function findStudySiteIdentities(route: ActivatedRouteSnapshot): SponsorStudySiteIdentity[] {
    return routeSnapshotExtensions.findData<SponsorStudySiteIdentity[]>(route, routeData.studySiteIdentities);
}

export function findSelectedStudy(route: ActivatedRouteSnapshot): SponsorStudy {
    return routeSnapshotExtensions.findData<SponsorStudy>(route, routeData.selectedStudy);
}

export function findSelectedSite(route: ActivatedRouteSnapshot): Site {
    return routeSnapshotExtensions.findData<Site>(route, routeData.selectedSite);
}

export function findTabName(route: ActivatedRouteSnapshot): string {
    return routeSnapshotExtensions.findParameter<string>(route, routeParams.tabName);
}

export function findUser(route: ActivatedRouteSnapshot): User {
    return routeSnapshotExtensions.findData<User>(route, routeData.user);
}

export function findFilterDefaultDateRange(route: ActivatedRouteSnapshot): (s: Settings) => number {
    return routeSnapshotExtensions.findData<(s: Settings) => number>(route, routeData.filterDefaultDateRange);
}

export function findSiteUrlParam(route: ActivatedRouteSnapshot): string {
    return routeSnapshotExtensions.findParameter<string>(route, routeParams.siteUrlParam);
}

export function findStudyUrlParam(route: ActivatedRouteSnapshot): string {
    return routeSnapshotExtensions.findParameter<string>(route, routeParams.studyUrlParam);
}

export function findAccessionNo(route: ActivatedRouteSnapshot): string {
    return routeSnapshotExtensions.findQueryParams<string>(route, routeParams.accessionNumber);
}
